import { useCallback } from 'react';
import useWaSecurityRepo from '../repository/WaSecurityRepo';
import { useWaAuthStore } from '../store/WaAuthStore';
import { useWaUserStore } from '../store/WaUserStore';
import { useWaFilterStore } from '../store/WaFilterStore';
import { useWaWorkplaceStore } from '../store/WaWorkplaceStore';
import { useWaProductStore } from '../store/WaProductStore';

function useWaSecurityService() {
  const { setAuthenticated } = useWaAuthStore();
  const { setUser } = useWaUserStore();
  const { resetFilters } = useWaFilterStore();
  const { setWorkplaces, setSelectedWorkplace } = useWaWorkplaceStore();
  const { setProducts, setSelectedProduct, setProductDetails } = useWaProductStore();
  const { getUserRequest, postLoginRequest, postLogoutRequest, patchUserLocationRequest } =
    useWaSecurityRepo();

  const waLoginUser = useCallback(
    async (username: string, password: string) => {
      const response = await postLoginRequest({ username, password });

      if (response !== null) {
        setAuthenticated(true);
      } else {
        setAuthenticated(false);
      }
    },

    [postLoginRequest, setAuthenticated]
  );

  const waLogoutUser = useCallback(async () => {
    await postLogoutRequest().finally(() => {
      resetFilters();
      setWorkplaces(null);
      setSelectedWorkplace(null);
      setProducts([]);
      setSelectedProduct(null);
      setProductDetails(null);
      setUser(null);
      setAuthenticated(false);
    });
  }, [
    postLogoutRequest,
    resetFilters,
    setAuthenticated,
    setUser,
    setWorkplaces,
    setSelectedWorkplace,
    setProducts,
    setSelectedProduct,
    setProductDetails,
  ]);

  const waGetUser = useCallback(async () => {
    await getUserRequest().then((user) => {
      // For Okinichi members, overwite branch code to '  ' (aliasBranchCode).
      if (user?.branchCode === '30' && user.salesOfficeCode === '306680') {
        user.branchCode = '  ';
      }

      setUser(user);
    });
  }, [getUserRequest, setUser]);

  const waUpdateUserLocation = useCallback(async (latitude: number, longitude: number) => {
    await patchUserLocationRequest({ latitude, longitude }).then((user) => {
      if (user) {
        setUser(user);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    waLoginUser,
    waLogoutUser,
    waGetUser,
    waUpdateUserLocation,
  };
}

export default useWaSecurityService;
